/**
 * LOOKING GLASS — Main App Component
 * V0.6: React 18 + SQLite + Rich Text + Spaces + Tags + AI + ScratchPad
 *       + Selection toolbar + Folder modal + rich Lightbox
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { useStore } from '../store/useStore.js';
import { HistoryManager, AddItemCommand, DeleteItemCommand, MoveItemCommand, UpdateItemCommand, StackCommand, FolderCommand } from '../history/HistoryManager.js';
import LiquidGlassSidebar from '../components/LiquidGlassSidebar.jsx';
import { Canvas } from '../canvas/Canvas.jsx';
import { ExportDialog } from '../utils/export/ExportDialog.jsx';
import { Lightbox } from '../ui/Lightbox.jsx';
import { ScratchPad } from '../ui/ScratchPad.jsx';
import { DropZoneHandler } from '../ui/DropZoneHandler.jsx';
import { Toolbar } from '../ui/Toolbar.jsx';
import { AISummarisePanel } from '../ui/AISummarisePanel.jsx';
import { fetchMetadata } from '../utils/meta-fetcher.js';
import { SmartContextMenu } from '../components/ContextMenu.jsx';
import { TagFilterBar, TagsPanel } from '../ui/TagsSystem.jsx';
import { RatingFilterBar, RatingPanel } from '../ui/RatingSystem.jsx';
import { CommandPalette } from '../ui/CommandPalette.jsx';
import { SpacesManager } from '../ui/SpacesManager.jsx';
import { FolderViewModal } from '../ui/FolderViewModal.jsx';
import { FicharioStyleEditor } from '../ui/FicharioStyleEditor.jsx';
import LiquidOrb from '../ui/LiquidOrb.jsx';
import { OnboardingTour } from '../ui/OnboardingTour.jsx';
import { ImmichPanel } from '../ui/ImmichPanel.jsx';
import VersionTimeline from '../history/VersionTimeline.jsx';
import TokenPreviewPanel from '../ui/TokenPreviewPanel.jsx';

export function App() {
  const initialized = useRef(false);
  const history = useRef(new HistoryManager(100));

  const {
    init,
    loadCanvas,
    canvasId,
    viewport,
    setViewport,
    items,
    selectedIds,
    selectItem,
    setSelection,
    clearSelection,
    activeFilters,
    toggleFilter,
    getFilteredItems,
    addNote,
    addUrl,
    addImage,
    addAudio,
    addVideo,
    addPDF,
    addWebClipScreenshot,
    deleteSelected,
    updateItem,
    deleteItem,
    search,
    clearSearch,
    searchQuery,
    exportDialogOpen,
    canvasName,
    undoCounts,
    createStack,
    addToStack,
    unstackToCanvas,
    createFolder,
    addToFolder,
    removeFromFolder,
    removeFromStack,
    dissolveStack,
    dissolveFolder,
    unfolderToCanvas,
    renameFolder,
    updateFolderDescription,
    arrangeMasonry,
    activeTagFilters,
    toggleTagFilter,
    clearTagFilters,
    ratingFilter,
    setRatingFilter,
    clearRatingFilter,
    spaces,
    activeSpaceId,
    seedDemoCanvas,
    hasSeenOnboarding,
    markOnboardingSeen,
  } = useStore();

  const [lightboxItem, setLightboxItem] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [aiSummarise, setAiSummarise] = useState(null);
  const [spacesOpen, setSpacesOpen] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [showRatings, setShowRatings] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [openFolderId, setOpenFolderId] = useState(null);
  const [ficharioStyleTargetId, setFicharioStyleTargetId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showImmich, setShowImmich] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showTokens, setShowTokens] = useState(false);

  // Initialize
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      (async () => {
        await init();
        // Onboarding: seed demo canvas if empty and first launch
        if (items.length === 0 && !hasSeenOnboarding) {
          await seedDemoCanvas();
          setShowOnboarding(true);
        }
      })();
    }
  }, [init, items.length, hasSeenOnboarding, seedDemoCanvas]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((v) => !v);
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedIds.size > 0) {
          e.preventDefault();
          handleDeleteSelected();
        }
        return;
      }
      if (e.key === 'n') {
        addNote();
        return;
      }
      if (e.key === 'Escape') {
        clearSelection();
        clearSearch();
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIds, search, clearSearch, clearSelection, addNote]);

  const handleUndo = useCallback(async () => {
    const result = history.current.undo();
    if (!result) return;
    const { command, result: data } = result;
    const state = useStore.getState();

    switch (command.type) {
      case 'add': {
        const newItems = state.items.filter((i) => i.id !== command.item.id);
        useStore.setState({ items: newItems });
        break;
      }
      case 'delete': {
        useStore.setState({ items: [...state.items, command.item] });
        break;
      }
      case 'move': {
        const newItems = state.items.map((i) =>
          i.id === command.itemId ? { ...i, x: data.x, y: data.y } : i
        );
        useStore.setState({ items: newItems });
        break;
      }
      case 'update': {
        const newItems = state.items.map((i) =>
          i.id === command.itemId ? { ...i, ...data } : i
        );
        useStore.setState({ items: newItems });
        break;
      }
      case 'stack': {
        // Undo: remove the stack, restore original items
        const restored = data.restoredItems.map((item, i) => ({
          ...item,
          z_index: (command.stackItem.z_index || 0) + i,
          updated_at: Date.now(),
        }));
        for (const item of restored) {
          await useStore.getState().addItem(item);
        }
        await useStore.getState().deleteItem(data.stackToRemove);
        break;
      }
      case 'folder': {
        // Undo: remove the folder, restore original items
        const restored = data.restoredItems.map((item, i) => ({
          ...item,
          z_index: (command.folderItem.z_index || 0) + i,
          updated_at: Date.now(),
        }));
        for (const item of restored) {
          await useStore.getState().addItem(item);
        }
        await useStore.getState().deleteItem(data.folderToRemove);
        break;
      }
    }
    useStore.setState({ undoCounts: history.current.getCounts() });
  }, []);

  const handleRedo = useCallback(async () => {
    const result = history.current.redo();
    if (!result) return;
    const { command } = result;
    const state = useStore.getState();

    switch (command.type) {
      case 'add': {
        useStore.setState({ items: [...state.items, command.item] });
        break;
      }
      case 'delete': {
        const newItems = state.items.filter((i) => i.id !== command.item.id);
        useStore.setState({ items: newItems });
        break;
      }
      case 'move': {
        const newItems = state.items.map((i) =>
          i.id === command.itemId ? { ...i, x: command.newX, y: command.newY } : i
        );
        useStore.setState({ items: newItems });
        break;
      }
      case 'update': {
        const newItems = state.items.map((i) =>
          i.id === command.itemId ? { ...i, ...command.newData } : i
        );
        useStore.setState({ items: newItems });
        break;
      }
      case 'stack': {
        // Redo: re-create the stack from stored source items
        const sourceItems = command.sourceItems;
        await useStore.getState().createStack(sourceItems.map(i => i.id));
        break;
      }
      case 'folder': {
        // Redo: re-create the folder from stored source items
        const sourceItems = command.sourceItems;
        await useStore.getState().createFolder(sourceItems.map(i => i.id), command.folderItem.content?.title || 'Folder name', command.folderItem.content?.description || '');
        break;
      }
    }
    useStore.setState({ undoCounts: history.current.getCounts() });
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    const state = useStore.getState();
    for (const id of state.selectedIds) {
      const item = state.items.find((i) => i.id === id);
      if (item) {
        history.current.push(new DeleteItemCommand(item, state.items));
      }
      await deleteItem(id);
    }
    useStore.setState({ undoCounts: history.current.getCounts() });
  }, [deleteItem]);

  const handleZoomIn = useCallback(() => {
    const newScale = Math.min(3, viewport.scale * 1.2);
    setViewport({ ...viewport, scale: newScale });
  }, [viewport, setViewport]);

  const handleZoomOut = useCallback(() => {
    const newScale = Math.max(0.1, viewport.scale / 1.2);
    setViewport({ ...viewport, scale: newScale });
  }, [viewport, setViewport]);

  const handleFit = useCallback(() => {
    // Fit all visible items into view with padding
    const items = getFilteredItems();
    if (items.length === 0) {
      setViewport({ x: 0, y: 0, scale: 1 });
      return;
    }
    const padding = 80;
    const xs = items.map((i) => i.x);
    const ys = items.map((i) => i.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const maxX = Math.max(...xs.map((x, i) => x + (items[i].width || 280)));
    const maxY = Math.max(...ys.map((y, i) => y + (items[i].height || 180)));
    const contentW = maxX - minX;
    const contentH = maxY - minY;
    // Use a sensible default viewport size based on window
    const vpW = window.innerWidth - 320; // subtract sidebar
    const vpH = window.innerHeight - 80;
    const scale = Math.min(2, Math.max(0.1, Math.min(vpW / (contentW + padding * 2), vpH / (contentH + padding * 2))));
    const cx = minX + contentW / 2;
    const cy = minY + contentH / 2;
    setViewport({
      x: vpW / 2 - cx * scale,
      y: vpH / 2 - cy * scale,
      scale,
    });
  }, [viewport, setViewport, getFilteredItems]);

  const handleExport = useCallback(() => {
    useStore.setState({ exportDialogOpen: true });
  }, []);

  const handleImport = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const text = await file.text();
      try {
        const data = JSON.parse(text);
        const state = useStore.getState();
        await state.importData(data);
      } catch (err) {
        toast.error('Import failed: ' + err.message);
      }
    };
    input.click();
  }, []);

  const handleItemMove = useCallback((id, x, y) => {
    const item = useStore.getState().items.find((i) => i.id === id);
    if (item) {
      history.current.push(new MoveItemCommand(id, item.x, item.y, x, y));
      updateItem(id, { x, y });
      useStore.setState({ undoCounts: history.current.getCounts() });
    }
  }, [updateItem]);

  const handleItemSave = useCallback((id, updates) => {
    const item = useStore.getState().items.find((i) => i.id === id);
    if (item) {
      history.current.push(new UpdateItemCommand(id, { ...item }, { ...item, ...updates }));
      updateItem(id, updates);
      useStore.setState({ undoCounts: history.current.getCounts() });
    }
  }, [updateItem]);

  // ── Selection toolbar handlers ─────────────────────────────────────

  const handleColorSelected = useCallback((hex) => {
    const state = useStore.getState();
    state.selectedIds.forEach((id) => {
      updateItem(id, { meta: { color: hex } });
    });
  }, [updateItem]);

  const handleCopyLinkSelected = useCallback(() => {
    const state = useStore.getState();
    const links = [...state.selectedIds]
      .map((id) => state.items.find((i) => i.id === id))
      .filter((i) => i?.content?.url)
      .map((i) => i.content.url);
    if (links.length) {
      navigator.clipboard?.writeText(links.join('\n'));
    }
  }, []);

  // ── Folder modal handlers ──────────────────────────────────────────

  const openFolder = items.find((i) => i.id === openFolderId);

  const handleCloseFolderModal = useCallback(() => setOpenFolderId(null), []);

  const handleRemoveFromFolder = useCallback(
    (childId) => {
      if (!openFolderId) return;
      removeFromFolder(openFolderId, childId);
    },
    [openFolderId, removeFromFolder],
  );

  const handleEmptyFolder = useCallback(() => {
    if (!openFolderId) return;
    unfolderToCanvas(openFolderId);
    setOpenFolderId(null);
  }, [openFolderId, unfolderToCanvas]);

  // Context menu action handler
  const handleContextAction = useCallback(async (action, item) => {
    switch (action) {
      case 'open':
        window.open(item.content?.url, '_blank');
        break;
      case 'copy-link':
        navigator.clipboard.writeText(item.content?.url || '');
        break;
      case 'stack': {
        const state = useStore.getState();
        const sourceItems = [...selectedIds].map((id) => state.items.find((i) => i.id === id)).filter(Boolean);
        const stackItem = await createStack([...selectedIds]);
        if (stackItem) {
          history.current.push(new StackCommand(stackItem, sourceItems));
          useStore.setState({ undoCounts: history.current.getCounts() });
        }
        break;
      }
      case 'folder': {
        const state = useStore.getState();
        const sourceItems = [...selectedIds].map((id) => state.items.find((i) => i.id === id)).filter(Boolean);
        const folderItem = await createFolder([...selectedIds], 'Folder name', '');
        if (folderItem) {
          history.current.push(new FolderCommand(folderItem, sourceItems));
          useStore.setState({ undoCounts: history.current.getCounts() });
        }
        break;
      }
      case 'open-folder':
        setOpenFolderId(item.id);
        break;
      case 'unstack':
        unstackToCanvas(item.id);
        break;
      case 'remove-from-folder':
        // Used inside FolderViewModal — remove this specific child from its parent folder
        if (openFolderId) {
          removeFromFolder(openFolderId, item.id);
        }
        break;
      case 'remove-from-stack':
        // Used inside a stack view — remove this specific child from its parent stack
        // When triggered from the stack card itself (no child context), dissolve
        dissolveStack(item.id);
        break;
      case 'dissolve':
        if (item.type === 'folder') {
          dissolveFolder(item.id);
        } else if (item.type === 'stack') {
          dissolveStack(item.id);
        }
        break;
      case 'summarise':
        setAiSummarise({ mode: 'card', item });
        break;
      case 'archive':
        updateItem(item.id, { meta: { archived: true } });
        break;
      case 'delete': {
        const state = useStore.getState();
        const target = state.items.find((i) => i.id === item.id);
        if (target) {
          history.current.push(new DeleteItemCommand(target, state.items));
        }
        deleteItem(target?.id ?? item.id);
        break;
      }
      case 'edit-tags':
        // Tag editor is inline on card — no-op at app level
        break;
      case 'fichario-add-page':
        useStore.getState().ficharioAddPage(item.id);
        break;
      case 'fichario-style':
        setFicharioStyleTargetId(item.id);
        break;
      case 'fichario-duplicate-page': {
        const activeId = item.content?.activePageId;
        if (activeId) useStore.getState().ficharioDuplicatePage(item.id, activeId);
        break;
      }
      case 'fichario-extract-page': {
        const activeId = item.content?.activePageId;
        if (activeId) useStore.getState().ficharioExtractPage(item.id, activeId);
        break;
      }
      case 'fichario-place': {
        // Dock this (usually single-page) Fichário onto the nearest other Fichário.
        const state = useStore.getState();
        const binders = state.items.filter((i) => i.type === 'fichario' && i.id !== item.id);
        if (binders.length === 0) break;
        let nearest = binders[0];
        let best = Infinity;
        for (const b of binders) {
          const d = Math.hypot(b.x - item.x, b.y - item.y);
          if (d < best) { best = d; nearest = b; }
        }
        state.ficharioMergeInto(item.id, nearest.id);
        break;
      }
      case 'color-none':
        updateItem(item.id, { meta: { color: null } });
        break;
      default:
        if (action.startsWith('color-')) {
          const COLORS = ['#D71921', '#F59E0B', '#22C55E', '#3B82F6', '#8B5CF6'];
          updateItem(item.id, { meta: { color: COLORS[parseInt(action.split('-')[1])] } });
        }
    }
  }, [selectedIds, createStack, createFolder, unstackToCanvas, removeFromFolder, removeFromStack, dissolveStack, dissolveFolder, updateItem, deleteItem, openFolderId]);

  // ── Stack/Folder history wrappers ──────────────────────────────────

  const handleAddToStack = useCallback(async (newItemId, stackItemId) => {
    const state = useStore.getState();
    const newItem = state.items.find((i) => i.id === newItemId);
    const stackItem = state.items.find((i) => i.id === stackItemId);
    if (!newItem || !stackItem) return;
    await addToStack(newItemId, stackItemId);
    // Push a command that can undo the addition
    const updatedStack = useStore.getState().items.find((i) => i.id === stackItemId);
    if (updatedStack) {
      history.current.push(new StackCommand(updatedStack, [newItem]));
      useStore.setState({ undoCounts: history.current.getCounts() });
    }
  }, [addToStack]);

  const handleAddToFolder = useCallback(async (newItemId, folderItemId) => {
    const state = useStore.getState();
    const newItem = state.items.find((i) => i.id === newItemId);
    const folderItem = state.items.find((i) => i.id === folderItemId);
    if (!newItem || !folderItem) return;
    await addToFolder(newItemId, folderItemId);
    // Push a command that can undo the addition
    const updatedFolder = useStore.getState().items.find((i) => i.id === folderItemId);
    if (updatedFolder) {
      history.current.push(new FolderCommand(updatedFolder, [newItem]));
      useStore.setState({ undoCounts: history.current.getCounts() });
    }
  }, [addToFolder]);

  // Fichário merge handler (drag a card onto a Fichário card to merge it in)
  const handleAddToFichario = useCallback(async (sourceId, targetId) => {
    await useStore.getState().ficharioMergeInto(sourceId, targetId);
  }, []);

  // filteredItems must be computed before any useCallback that references it
  // (temporal dead zone fix - this was causing ReferenceError)
  const filteredItems = getFilteredItems();
  const handleDrop = useCallback(async (drops) => {
    for (const drop of drops) {
      switch (drop.kind) {
        case 'image':
          await addImage(drop.objectUrl);
          break;
        case 'video':
          await addVideo(drop.file, drop.objectUrl);
          break;
        case 'pdf':
          await addPDF(drop.file);
          break;
        case 'audio':
          await addAudio();
          break;
        case 'url': {
          // Fetch metadata and add as web clip screenshot
          try {
            const meta = await fetchMetadata(drop.url);
            await addWebClipScreenshot(drop.url, meta);
          } catch {
            await addUrl(drop.url);
          }
          break;
        }
        case 'text':
          await addNote();
          break;
      }
    }
  }, [addImage, addVideo, addPDF, addAudio, addWebClipScreenshot, addUrl, addNote]);

  // AI organise handler
  const handleAIOrganise = useCallback(() => {
    setAiSummarise({ mode: 'organise' });
  }, []);

  // AI cluster handler
  const handleAICluster = useCallback(() => {
    const selectedItems = filteredItems.filter(i => selectedIds.has(i.id));
    if (selectedItems.length > 1) {
      setAiSummarise({ mode: 'cluster', selectedItems });
    }
  }, [filteredItems, selectedIds]);

  // AI summarise handler for sidebar - requires a selected card
  const handleAISummarise = useCallback(() => {
    const sel = filteredItems.filter(i => selectedIds.has(i.id));
    if (sel.length === 1) {
      setAiSummarise({ mode: 'card', item: sel[0] });
    } else if (sel.length > 1) {
      setAiSummarise({ mode: 'cluster', selectedItems: sel });
    } else {
      toast('Select a card first to summarise, or multiple cards for cluster insights');
    }
  }, [filteredItems, selectedIds]);

  return (
    <div className="flex flex-col sm:flex-row" style={{ width: '100%', height: '100dvh', overflow: 'hidden', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: 'rgba(20,20,20,0.95)',
            color: '#fff',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.10)',
            backdropFilter: 'blur(16px)',
            fontFamily: 'var(--font-ui)',
            fontSize: '12px',
            marginBottom: 'env(safe-area-inset-bottom)',
          },
        }}
      />
      <LiquidGlassSidebar
        onSpacesOpen={() => setSpacesOpen(true)}
        onTagsOpen={() => setShowTags(true)}
        onRatingsOpen={() => setShowRatings(true)}
        onAIOrganise={handleAIOrganise}
        onAISummarise={handleAISummarise}
        onSearch={search}
        onAddNote={addNote}
        onAddUrl={addUrl}
        onExport={handleExport}
        onImmichOpen={() => setShowImmich(true)}
        onVersionsOpen={() => setShowVersions(true)}
        onTokensOpen={() => setShowTokens(true)}
      />
      <div data-main-content style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <DropZoneHandler viewport={viewport} onDrop={handleDrop}>
          <div style={{ overflowX: 'auto' }}>
            <TagFilterBar
              activeTagFilters={activeTagFilters}
              onToggleTag={toggleTagFilter}
              onClearTags={clearTagFilters}
            />
            <RatingFilterBar
              ratingFilter={ratingFilter}
              onClear={clearRatingFilter}
            />
          </div>
          <Canvas
            items={filteredItems}
            viewport={viewport}
            selectedIds={selectedIds}
            onViewportChange={setViewport}
            onSelectItem={selectItem}
            onSetSelection={setSelection}
            onClearSelection={clearSelection}
            onItemMove={handleItemMove}
            onItemSave={handleItemSave}
            onItemDelete={deleteItem}
            onLightbox={setLightboxItem}
            onCreateStack={createStack}
            onAddToStack={handleAddToStack}
            onCreateFolder={createFolder}
            onAddToFolder={handleAddToFolder}
            onAddToFichario={handleAddToFichario}
            onContextMenu={(item, x, y) => setContextMenu({ item, x, y })}
            onOpenFolder={setOpenFolderId}
            onColorSelected={handleColorSelected}
            onCopyLinkSelected={handleCopyLinkSelected}
            onDeleteSelected={handleDeleteSelected}
            onArrangeSelected={(ids) => arrangeMasonry(ids)}
          />
        </DropZoneHandler>

        {/* Floating Toolbar */}
        <Toolbar
          zoom={viewport.scale}
          searchQuery={searchQuery}
          onAddNote={addNote}
          onAddFichario={() => useStore.getState().addFichario()}
          onDelete={handleDeleteSelected}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFit={handleFit}
          onSearch={search}
          onSearchClear={clearSearch}
          onExport={handleExport}
          onImport={handleImport}
          canUndo={undoCounts.undo > 0}
          canRedo={undoCounts.redo > 0}
          selectedCount={selectedIds.size}
        />
      </div>

      {exportDialogOpen && (
        <ExportDialog
          onClose={() => useStore.setState({ exportDialogOpen: false })}
        />
      )}

      {lightboxItem && (
        <Lightbox
          item={lightboxItem}
          onClose={() => setLightboxItem(null)}
          onColor={(hex) => updateItem(lightboxItem.id, { meta: { color: hex } })}
        />
      )}

      {/* Folder expand modal */}
      {openFolder && (
        <FolderViewModal
          folder={openFolder}
          onClose={handleCloseFolderModal}
          onRemoveItem={handleRemoveFromFolder}
          onEmptyAll={handleEmptyFolder}
          onRename={(name) => renameFolder(openFolder.id, name)}
          onDescription={(desc) => updateFolderDescription(openFolder.id, desc)}
        />
      )}

      {/* Fichário style editor */}
      {ficharioStyleTargetId && (() => {
        const target = items.find((i) => i.id === ficharioStyleTargetId);
        if (!target) return null;
        return (
          <FicharioStyleEditor
            item={target}
            onClose={() => setFicharioStyleTargetId(null)}
          />
        );
      })()}

      {/* ScratchPad overlay */}
      <ScratchPad />

      {/* Context menu */}
      {contextMenu && (
        <SmartContextMenu
          isOpen
          x={contextMenu.x}
          y={contextMenu.y}
          item={contextMenu.item}
          selectedIds={selectedIds}
          onClose={() => setContextMenu(null)}
          onAction={handleContextAction}
        />
      )}

      {/* AI Summarise Panel */}
      {aiSummarise && (
        <AISummarisePanel
          mode={aiSummarise.mode}
          item={aiSummarise.item}
          selectedItems={aiSummarise.selectedItems || filteredItems.filter(i => selectedIds.has(i.id))}
          allItems={items}
          onClose={() => setAiSummarise(null)}
          onApplyOrganisation={(groups, tags) => {
            // Apply organisation — groups and tags
            console.log('Apply organisation:', groups, tags);
          }}
          onAddNote={(text) => addNote()}
        />
      )}

      {/* Spaces Manager */}
      <SpacesManager isOpen={spacesOpen} onClose={() => setSpacesOpen(false)} />

      {/* Tags Panel slide-over */}
      {showTags && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 'calc(var(--z-toolbar) + 1)', display: 'flex' }}>
          <div onClick={() => setShowTags(false)} style={{ flex: 1, background: 'rgba(0,0,0,0.25)' }} />
          <aside style={{
            width: '280px',
            height: '100%',
            background: 'var(--glass-frost)',
            backdropFilter: 'blur(var(--glass-blur-xl)) saturate(120%)',
            WebkitBackdropFilter: 'blur(var(--glass-blur-xl)) saturate(120%)',
            borderLeft: '1px solid var(--color-border)',
            boxShadow: '-4px 0 32px rgba(0,0,0,0.50)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 'env(safe-area-inset-bottom)'
          }}>
            <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-ui)', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>TAGS</span>
              <button onClick={() => setShowTags(false)} style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px', minWidth: '44px', minHeight: '44px' }}>✕</button>
            </div>
            <TagsPanel
              items={items}
              activeTagFilters={activeTagFilters}
              onToggleTag={toggleTagFilter}
              onClearTags={clearTagFilters}
            />
          </aside>
        </div>
      )}

      {/* Ratings Panel slide-over */}
      {showRatings && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 'calc(var(--z-toolbar) + 1)', display: 'flex' }}>
          <div onClick={() => setShowRatings(false)} style={{ flex: 1, background: 'rgba(0,0,0,0.25)' }} />
          <aside style={{
            width: '280px',
            height: '100%',
            background: 'var(--glass-frost)',
            backdropFilter: 'blur(var(--glass-blur-xl)) saturate(120%)',
            WebkitBackdropFilter: 'blur(var(--glass-blur-xl)) saturate(120%)',
            borderLeft: '1px solid var(--color-border)',
            boxShadow: '-4px 0 32px rgba(0,0,0,0.50)',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 'env(safe-area-inset-bottom)'
          }}>
            <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-ui)', fontSize: '10px', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>RATINGS</span>
              <button onClick={() => setShowRatings(false)} style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '4px', minWidth: '44px', minHeight: '44px' }}>✕</button>
            </div>
            <RatingPanel
              items={items}
              ratingFilter={ratingFilter}
              onSetRating={setRatingFilter}
              onClear={clearRatingFilter}
            />
          </aside>
        </div>
      )}

      {/* Command Palette */}
      {commandPaletteOpen && (
        <CommandPalette
          isOpen={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          onSearch={search}
          onAddNote={addNote}
          onAddUrl={addUrl}
          onClearSearch={clearSearch}
        />
      )}
      {/* Immich asset library */}
      <ImmichPanel isOpen={showImmich} onClose={() => setShowImmich(false)} />

      {/* Version history timeline */}
      {showVersions && (
        <div onClick={() => setShowVersions(false)} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, calc(100vw - 24px))', maxHeight: 'min(80dvh, 720px)', overflowY: 'auto', borderRadius: '16px', border: '1px solid var(--color-border)', background: 'rgba(20,20,20,0.96)', backdropFilter: 'blur(18px)', padding: '16px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontFamily: 'var(--font-ui)', fontSize: '11px', letterSpacing: '0.1em', color: 'var(--text-secondary)' }}>VERSION HISTORY</span>
              <button onClick={() => setShowVersions(false)} style={{ minWidth: '44px', minHeight: '44px', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>✕</button>
            </div>
            <VersionTimeline />
          </div>
        </div>
      )}

      {/* Design token live preview */}
      {showTokens && (
        <div onClick={() => setShowTokens(false)} style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(620px, calc(100vw - 24px))', maxHeight: 'min(80dvh, 720px)', overflowY: 'auto', borderRadius: '16px', border: '1px solid var(--color-border)', background: 'rgba(20,20,20,0.96)', backdropFilter: 'blur(18px)', padding: '16px', paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontFamily: 'var(--font-ui)', fontSize: '11px', letterSpacing: '0.1em', color: 'var(--text-secondary)' }}>DESIGN TOKENS</span>
              <button onClick={() => setShowTokens(false)} style={{ minWidth: '44px', minHeight: '44px', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>✕</button>
            </div>
            <TokenPreviewPanel />
          </div>
        </div>
      )}

      {/* AI Orb — bottom center */}
      <LiquidOrb />

      {/* Onboarding Tour — first launch only */}
      {showOnboarding && (
        <OnboardingTour
          onDismiss={() => {
            setShowOnboarding(false);
            markOnboardingSeen();
          }}
        />
      )}
    </div>
  );
}